import type { PlayerState } from "@/types/game";

// 环形座位：A1-B1-A2-B2 顺时针 0/1/2/3
// 死亡玩家不计入环，跳过。
// 距离 = 沿活人环顺时针/逆时针的最小步数。
export function aliveRing(players: PlayerState[]): PlayerState[] {
  return players
    .filter((p) => p.isAlive)
    .sort((a, b) => a.seat - b.seat);
}

// 计算两个活人之间的基础座位距离（不考虑武器加成）
// 同一玩家距离 0；相邻为 1；环上一共 N 人时最大距离 = floor(N/2)
export function baseDistance(
  players: PlayerState[],
  fromId: string,
  toId: string
): number {
  if (fromId === toId) return 0;
  const ring = aliveRing(players);
  const idx = ring.findIndex((p) => p.id === fromId);
  const jdx = ring.findIndex((p) => p.id === toId);
  if (idx < 0 || jdx < 0) return Infinity;
  const n = ring.length;
  if (n === 0) return Infinity;
  const cw = (jdx - idx + n) % n;
  const ccw = (idx - jdx + n) % n;
  return Math.min(cw, ccw);
}

// 攻击者武器攻距加成（仅装备的武器卡上有 rangeBonus 时生效）
// catalog 中武器的 rangeBonus 在装备时由 GameEngine 写入 weapon 卡上
export function weaponRangeBonus(attacker: PlayerState): number {
  return attacker.equip.weapon?.rangeBonus ?? 0;
}

// 实际距离判断：是否在射程内
//   reach = baseDistance + (attacker 武器 rangeBonus)
//   target 在 [1, reach] 内时可被指定
export function inRange(
  players: PlayerState[],
  attackerId: string,
  targetId: string
): boolean {
  const attacker = players.find((p) => p.id === attackerId);
  if (!attacker || !attacker.isAlive) return false;
  const target = players.find((p) => p.id === targetId);
  if (!target || !target.isAlive) return false;
  if (attackerId === targetId) return false;
  const base = baseDistance(players, attackerId, targetId);
  if (base === Infinity) return false;
  const bonus = attacker.equip.weapon?.rangeBonus ?? 0;
  return base >= 1 && base <= 1 + bonus; // 默认距离 1
}

// 枚举攻击者射程内所有目标 id
export function targetsInRange(
  players: PlayerState[],
  attackerId: string
): string[] {
  return players
    .filter((p) => p.isAlive && p.id !== attackerId)
    .filter((p) => inRange(players, attackerId, p.id))
    .map((p) => p.id);
}
